import ConfirmDialog from '@/components/ConfirmDialog';
import SortableHeader from '@/components/SortableHeader';
import TablePagination from '@/components/TablePagination';
import TagForm from '@/components/TagForm';
import { useToast } from '@/hooks/useToast';
import { tagsService } from '@/services/tagsService';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const ProjectTags = () => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useToast();
  const columnHelper = createColumnHelper();

  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showTagForm, setShowTagForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const handleSort = field => {
    setPagination(prev => ({
      ...prev,
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
      pageIndex: 0,
    }));
  };

  const loadTags = useCallback(
    async (page = 0, search = searchTerm) => {
      try {
        setIsLoading(true);
        const params = {
          page: page + 1,
          limit: pagination.pageSize,
          sortBy: pagination.sortBy,
          sortOrder: pagination.sortOrder,
        };

        const response = search
          ? await tagsService.searchTags(projectId, search, params)
          : await tagsService.getTags(projectId, params);

        const tagsList = Array.isArray(response)
          ? response
          : response.docs || [];
        setTags(tagsList);

        setPagination(prev => ({
          ...prev,
          pageIndex: page,
          total: Array.isArray(response)
            ? response.length
            : response.totalDocs || 0,
          totalPages: Array.isArray(response) ? 1 : response.totalPages || 0,
          hasNextPage: Array.isArray(response)
            ? false
            : response.hasNextPage || false,
          hasPrevPage: Array.isArray(response)
            ? false
            : response.hasPrevPage || false,
        }));
      } catch (error) {
        console.error('Error loading tags:', error);
        showError('Failed to load tags');
      } finally {
        setIsLoading(false);
      }
    },
    [
      projectId,
      searchTerm,
      pagination.pageSize,
      pagination.sortBy,
      pagination.sortOrder,
      showError,
    ]
  );

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const handleCreateOrUpdate = async tagData => {
    try {
      setIsProcessing(true);
      if (editingTag) {
        await tagsService.updateTag(projectId, editingTag._id, tagData);
        showSuccess('Tag updated successfully');
      } else {
        await tagsService.createTag(projectId, tagData);
        showSuccess('Tag created successfully');
      }
      setShowTagForm(false);
      setEditingTag(null);
      loadTags(pagination.pageIndex);
    } catch (error) {
      console.error('Error saving tag:', error);
      showError(error.response?.data?.message || 'Failed to save tag');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsProcessing(true);
      await tagsService.deleteTag(projectId, tagToDelete._id);
      showSuccess('Tag deleted successfully');
      setShowDeleteDialog(false);
      setTagToDelete(null);
      loadTags(0);
    } catch (error) {
      console.error('Error deleting tag:', error);
      showError(error.response?.data?.message || 'Failed to delete tag');
    } finally {
      setIsProcessing(false);
    }
  };

  // Debounce search term changes
  useEffect(() => {
    if (searchTerm === '') {
      loadTags(0, '');
      return;
    }

    const timeoutId = setTimeout(() => {
      loadTags(0, searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadTags]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: info => (
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: info.row.original.color || '#3B82F6' }}
            />
            <span className="font-medium text-gray-900">{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created',
        cell: info => {
          const val = info.getValue();
          return val ? new Date(val).toLocaleDateString() : '-';
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: info => (
          <div className="flex gap-3">
            <button
              onClick={() => {
                setEditingTag(info.row.original);
                setShowTagForm(true);
              }}
              className="text-primary-600 hover:text-primary-900 text-sm font-medium"
            >
              Edit
            </button>
            <button
              onClick={() => {
                setTagToDelete(info.row.original);
                setShowDeleteDialog(true);
              }}
              className="text-red-600 hover:text-red-900 text-sm font-medium"
            >
              Delete
            </button>
          </div>
        ),
      }),
    ],
    [columnHelper]
  );

  const table = useReactTable({
    data: tags,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: pagination.totalPages,
  });

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      <div className="bg-white shadow flex-shrink-0">
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tags</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage contact labels for this project
              </p>
            </div>
            <button
              onClick={() => {
                setEditingTag(null);
                setShowTagForm(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Add Tag
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 py-4 sm:px-6 lg:px-8">
          <div className="max-w-md">
            <input
              type="text"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col p-4 sm:p-6 lg:p-8">
        <div className="bg-white shadow rounded-lg overflow-hidden flex flex-col flex-1">
          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span className="text-gray-500">Loading tags...</span>
                </div>
              </div>
            ) : tags.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">
                  {searchTerm
                    ? 'No tags matching your search.'
                    : 'No tags found.'}
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => {
                      setEditingTag(null);
                      setShowTagForm(true);
                    }}
                    className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Create your first tag
                  </button>
                )}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 text-left">
                <thead className="bg-[#fcfcfd] sticky top-0 z-10 shadow-sm text-left">
                  <tr>
                    <SortableHeader
                      columnId="name"
                      label="Name"
                      sortBy={pagination.sortBy}
                      sortOrder={pagination.sortOrder}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      columnId="createdAt"
                      label="Created"
                      sortBy={pagination.sortBy}
                      sortOrder={pagination.sortOrder}
                      onSort={handleSort}
                    />
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-widest bg-gray-50"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Standard Pagination */}
          {!isLoading && tags.length > 0 && (
            <TablePagination
              pagination={pagination}
              onPageChange={pageIndex => loadTags(pageIndex)}
              onPageSizeChange={pageSize => {
                setPagination(prev => ({
                  ...prev,
                  pageSize,
                  pageIndex: 0,
                }));
                loadTags(0, searchTerm);
              }}
            />
          )}
        </div>
      </div>

      {/* Tag Form Modal Placeholder (Simplified using a fixed overlay for now) */}
      {showTagForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setShowTagForm(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <TagForm
                tag={editingTag}
                onSubmit={handleCreateOrUpdate}
                onCancel={() => setShowTagForm(false)}
                isLoading={isProcessing}
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Tag"
        message={`Are you sure you want to delete the tag "${tagToDelete?.name}"? This will remove it from all associated contacts.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isProcessing}
      />
    </div>
  );
};

export default ProjectTags;
